import EmailIcon from "@mui/icons-material/Email";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  CircularProgress,
  IconButton,
  Autocomplete as MuiAutocomplete,
  TextField as MuiTextField,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useAppSelector } from "app/hooks";
import settings from "app/settings";
import { PageHeader } from "common";
import { getBookingService } from "features/bookings/bookingApi";
import { getEmailTemplateService } from "features/email-templates/emailTemplateApi";
import { selectAccessToken } from "features/identity/identitySlice";
import I18n from "i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { EmailPreviewEndpoint, fetchEmailPreviewHtml } from "./emailPreviewApi";

const TABS: { endpoint: EmailPreviewEndpoint; label: string }[] = [
  { endpoint: "review", label: "Értékelés" },
  { endpoint: "payment-link", label: "Fizetési Link" },
  { endpoint: "payment-success", label: "Fizetés Sikeres" },
  { endpoint: "bulk-info", label: "Tömeges Info" },
];

interface BookingOption {
  id: number;
  label: string;
}

interface TemplateOption {
  value: number;
  label: string;
}

export default function EmailPreviewPage() {
  const accessToken = useAppSelector(selectAccessToken);

  const [tabIndex, setTabIndex] = useState(0);
  const [lang, setLang] = useState("hu");

  const [booking, setBooking] = useState<BookingOption | null>(null);
  const [bookingInputValue, setBookingInputValue] = useState("");
  const [bookingOptions, setBookingOptions] = useState<BookingOption[]>([]);
  const [bookingLoading, setBookingLoading] = useState(false);

  const [template, setTemplate] = useState<TemplateOption | null>(null);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);

  const [htmlContent, setHtmlContent] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const debounceRef = useRef<any>(null);
  const currentEndpoint = TABS[tabIndex].endpoint;
  const hasLang =
    currentEndpoint === "payment-link" || currentEndpoint === "payment-success";
  const hasBulkTemplate = currentEndpoint === "bulk-info";

  // Load latest booking on mount
  useEffect(() => {
    const service = getBookingService();
    service.paginateItems("sort[]=-id&per_page=1").then(res => {
      if (res.success && res.data?.length) {
        const first = res.data[0];
        const opt: BookingOption = {
          id: first.id,
          label: `${first.booking_number} – ${first.name}`,
        };
        setBookingOptions([opt]);
        setBooking(opt);
      }
    });
  }, []);

  // Load template options on mount
  useEffect(() => {
    const service = getEmailTemplateService();
    service.loadAutocomplete().then((opts: any[]) => {
      const mapped: TemplateOption[] = opts.map(o => ({
        value: o.value ?? o.id,
        label: o.label ?? o.name,
      }));
      setTemplateOptions(mapped);
      if (mapped.length > 0) setTemplate(mapped[0]);
    });
  }, []);

  // Debounced booking search
  function searchBookings(keyword: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBookingLoading(true);
      const service = getBookingService();
      const { success, data } = await service.paginateItems(
        `keyword=${encodeURIComponent(keyword)}&per_page=20`,
      );
      setBookingLoading(false);
      if (success && data) {
        setBookingOptions(
          data.map((b: any) => ({
            id: b.id,
            label: `${b.booking_number} – ${b.name}`,
          })),
        );
      }
    }, 500);
  }

  const loadPreview = useCallback(async () => {
    if (hasBulkTemplate && !template) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const html = await fetchEmailPreviewHtml(
        currentEndpoint,
        {
          booking_id: booking?.id ?? null,
          template_id: hasBulkTemplate ? (template?.value ?? null) : null,
          lang: hasLang ? lang : undefined,
        },
        settings.apiHost!,
        accessToken!,
      );
      setHtmlContent(html);
    } catch (e: any) {
      setPreviewError(e.message || "Hiba történt az előnézet betöltésekor");
      setHtmlContent("");
    } finally {
      setPreviewLoading(false);
    }
  }, [
    currentEndpoint,
    booking,
    lang,
    template,
    hasLang,
    hasBulkTemplate,
    accessToken,
  ]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader avatar={<EmailIcon />} title={I18n.t("EmailPreview.title")} />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        {/* Booking selector */}
        <MuiAutocomplete
          sx={{ minWidth: 320 }}
          options={bookingOptions}
          value={booking}
          inputValue={bookingInputValue}
          loading={bookingLoading}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_e, val) => setBooking(val)}
          onInputChange={(_e, val, reason) => {
            setBookingInputValue(val);
            if (reason === "input" && val.length >= 2) searchBookings(val);
          }}
          renderInput={params => (
            <MuiTextField
              {...params}
              label={I18n.t("EmailPreview.booking")}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {bookingLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Lang selector */}
        {hasLang && (
          <ToggleButtonGroup
            value={lang}
            exclusive
            size="small"
            onChange={(_e, val) => val && setLang(val)}
          >
            <ToggleButton value="hu">HU</ToggleButton>
            <ToggleButton value="en">EN</ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Template selector for bulk-info */}
        {hasBulkTemplate && (
          <MuiAutocomplete
            sx={{ minWidth: 280 }}
            options={templateOptions}
            value={template}
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_e, val) => setTemplate(val)}
            renderInput={params => (
              <MuiTextField
                {...params}
                label={I18n.t("EmailPreview.template")}
                size="small"
              />
            )}
          />
        )}

        <IconButton
          onClick={loadPreview}
          disabled={previewLoading}
          size="small"
        >
          <RefreshIcon />
        </IconButton>

        {previewLoading && <CircularProgress size={20} />}
      </Box>

      <Tabs
        value={tabIndex}
        onChange={(_e, v) => setTabIndex(v)}
        sx={{ mb: 1 }}
      >
        {TABS.map(t => (
          <Tab key={t.endpoint} label={t.label} />
        ))}
      </Tabs>

      {previewError && (
        <Typography color="error" sx={{ mb: 1 }}>
          {previewError}
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <iframe
          title="email-preview"
          srcDoc={htmlContent}
          style={{
            width: "100%",
            height: "100%",
            minHeight: 600,
            border: "none",
          }}
        />
      </Box>
    </Box>
  );
}
